import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api.js";
import type { LibraryBook } from "../lib/types.js";
import { Icon } from "../components/Icon.js";
import { BrandMark } from "../components/BrandMark.js";
import { useToast } from "../components/Toast.js";
import { Modal } from "../components/Modal.js";
import { AccountMenu } from "../auth/AccountMenu.js";
import { formatNumber, formatRelativeTime, statusLabel } from "../lib/format.js";
import { CoverArt } from "./CoverArt.js";
import { ColoringWizard } from "../coloring/ColoringWizard.js";
import { DeleteBookDialog } from "../settings/DeleteBookDialog.js";
import type { ProjectType } from "../lib/types.js";

interface Props {
  onOpenBook: (projectId: string, type: ProjectType) => void;
}

/** The ClovaScribe home: a visual shelf of the author's books. */
export function Library({ onOpenBook }: Props) {
  const toast = useToast();
  const [books, setBooks] = useState<LibraryBook[] | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<LibraryBook | null>(null);

  const load = useCallback(async () => {
    try {
      const { books: list } = await api.library();
      setBooks(list);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load your library");
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  // Group by series (books sharing a series name), standalone books last.
  const { series, standalone } = useMemo(() => {
    const groups = new Map<string, LibraryBook[]>();
    const solo: LibraryBook[] = [];
    for (const book of books ?? []) {
      const name = book.project.seriesName?.trim();
      if (name) {
        const list = groups.get(name) ?? [];
        list.push(book);
        groups.set(name, list);
      } else {
        solo.push(book);
      }
    }
    // A "series" of one is just a standalone book with a label; show it inline.
    const realSeries: { name: string; books: LibraryBook[] }[] = [];
    for (const [name, list] of groups) {
      if (list.length > 1) {
        list.sort((a, b) => (a.project.seriesNumber ?? 0) - (b.project.seriesNumber ?? 0));
        realSeries.push({ name, books: list });
      } else {
        solo.push(...list);
      }
    }
    realSeries.sort((a, b) => a.name.localeCompare(b.name));
    solo.sort((a, b) => new Date(b.stats.lastEditedAt).getTime() - new Date(a.stats.lastEditedAt).getTime());
    return { series: realSeries, standalone: solo };
  }, [books]);

  return (
    <div className="library">
      <header className="topbar glass">
        <BrandMark size={30} />
        <div className="spacer" />
        <button className="btn btn-sm btn-primary" onClick={() => setShowNew(true)}>
          <Icon name="plus" size={15} /> New book
        </button>
        <AccountMenu />
      </header>

      <div className="library-scroll">
        {books === null ? (
          <div className="library-loading"><Icon name="book" size={26} className="spin" style={{ color: "var(--accent)" }} /></div>
        ) : (
          <div className="library-inner">
            <div className="library-heading">
              <h1>Your library</h1>
              <p className="muted">{books.length} {books.length === 1 ? "book" : "books"}</p>
            </div>

            {series.map((s) => (
              <section key={s.name} className="series-section">
                <div className="series-head">
                  <Icon name="layers" size={15} style={{ color: "var(--accent)" }} />
                  <h2>{s.name}</h2>
                  <span className="chip">{s.books.length} books</span>
                </div>
                <div className="book-grid">
                  {s.books.map((b) => (
                    <BookCard key={b.project.id} book={b} onOpen={onOpenBook} onDelete={setDeleteTarget} showSeriesNumber />
                  ))}
                </div>
              </section>
            ))}

            {standalone.length > 0 && (
              <section className="series-section">
                {series.length > 0 && (
                  <div className="series-head">
                    <Icon name="book" size={15} style={{ color: "var(--text-muted)" }} />
                    <h2>Standalone</h2>
                  </div>
                )}
                <div className="book-grid">
                  {standalone.map((b) => (
                    <BookCard key={b.project.id} book={b} onOpen={onOpenBook} onDelete={setDeleteTarget} />
                  ))}
                  <button className="new-book-card" onClick={() => setShowNew(true)}>
                    <div className="new-book-plus"><Icon name="plus" size={28} /></div>
                    <span>New book</span>
                  </button>
                </div>
              </section>
            )}

            {books.length === 0 && (
              <div className="book-grid">
                <button className="new-book-card" onClick={() => setShowNew(true)}>
                  <div className="new-book-plus"><Icon name="plus" size={28} /></div>
                  <span>New book</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {showNew && (
        <NewProjectModal
          onClose={() => setShowNew(false)}
          onCreated={async (id, type) => {
            setShowNew(false);
            await load();
            onOpenBook(id, type);
          }}
        />
      )}
      {deleteTarget && (
        <DeleteBookDialog
          project={deleteTarget.project}
          onCancel={() => setDeleteTarget(null)}
          onDeleted={async () => { setDeleteTarget(null); await load(); }}
        />
      )}
    </div>
  );
}

function BookCard({ book, onOpen, onDelete, showSeriesNumber }: {
  book: LibraryBook;
  onOpen: (id: string, type: ProjectType) => void;
  onDelete: (book: LibraryBook) => void;
  showSeriesNumber?: boolean;
}) {
  const { project, stats, coloring } = book;
  // Big banner at the top of a series book's cover: "#2 · STILL".
  const volBanner = showSeriesNumber && project.seriesNumber != null
    ? `#${project.seriesNumber}${project.volumeTitle ? ` · ${project.volumeTitle}` : ""}`
    : null;
  return (
    <div className="book-card-wrap">
      <button className="book-card" onClick={() => onOpen(project.id, project.type)}>
        <div className="book-cover-wrap">
          {volBanner && <span className="book-vol-banner">{volBanner}</span>}
          {project.type === "coloring" && coloring?.coverVariantId ? (
            <div className="cover-art cover-art-image">
              <img src={`/api/projects/${project.id}/coloring/images/${coloring.coverVariantId}`} alt={`${project.title} cover`} />
            </div>
          ) : (
            <CoverArt project={project} />
          )}
          {project.type === "coloring" && <span className="book-type-badge">Coloring</span>}
        </div>
        <div className="book-meta">
          <div className="book-title truncate">{project.title}</div>
          {showSeriesNumber && project.seriesName && (
            <div className="book-series-line truncate">
              {project.seriesName}
              {project.seriesNumber != null ? ` · Vol. ${project.seriesNumber}` : ""}
              {project.volumeTitle ? `: ${project.volumeTitle}` : ""}
            </div>
          )}
          {project.author && <div className="book-author truncate">{project.author}</div>}
          <div className="book-stats">
            <span className={`chip chip-accent book-status`}>{statusLabel(project.status)}</span>
            {coloring ? (
              <span className="faint">{coloring.generatedCount}/{coloring.pageCount} pages</span>
            ) : (
              <span className="faint">{formatNumber(stats.wordCount)} words</span>
            )}
          </div>
          <div className="faint book-edited">Edited {formatRelativeTime(stats.lastEditedAt)}</div>
        </div>
      </button>
      <button className="book-card-del" title="Delete book" aria-label={`Delete ${project.title}`}
        onClick={(e) => { e.stopPropagation(); onDelete(book); }}>
        <Icon name="trash" size={14} />
      </button>
    </div>
  );
}

function NewProjectModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string, type: ProjectType) => void }) {
  const [kind, setKind] = useState<ProjectType | null>(null);
  if (kind === null) {
    return (
      <Modal title="New project" subtitle="What are you making?" onClose={onClose} width={560}>
        <div className="export-grid">
          <button className="export-option project-type-option" onClick={() => setKind("manuscript")}>
            <Icon name="book" size={22} style={{ color: "var(--accent)" }} />
            <div className="col" style={{ minWidth: 0 }}>
              <span className="strong">Book / Novel</span>
              <span className="faint" style={{ fontSize: 11.5 }}>A manuscript with chapters, scenes, a story bible and Scribe AI.</span>
            </div>
          </button>
          <button className="export-option project-type-option" onClick={() => setKind("coloring")}>
            <Icon name="image" size={22} style={{ color: "var(--accent)" }} />
            <div className="col" style={{ minWidth: 0 }}>
              <span className="strong">Coloring Book</span>
              <span className="faint" style={{ fontSize: 11.5 }}>Plan, generate and print a consistent AI-assisted coloring book.</span>
            </div>
          </button>
        </div>
      </Modal>
    );
  }
  if (kind === "coloring") {
    return <ColoringWizard onClose={onClose} onCreated={(id) => onCreated(id, "coloring")} />;
  }
  return <NovelForm onClose={onClose} onCreated={(id) => onCreated(id, "manuscript")} />;
}

function NovelForm({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const toast = useToast();
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [genre, setGenre] = useState("");
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!title.trim()) {
      toast.error("A title is required.");
      return;
    }
    setSaving(true);
    try {
      const { project } = await api.createProject({
        title: title.trim(),
        author: author.trim() || undefined,
        genre: genre.trim() || undefined
      });
      toast.success("Book created.");
      onCreated(project.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create book.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="New book"
      subtitle="Start a new project. A first chapter and scene are created for you."
      onClose={onClose}
      width={480}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={create} disabled={saving}>
            {saving ? "Creating…" : "Create book"}
          </button>
        </>
      }
    >
      <div className="field">
        <label className="label">Title</label>
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
      </div>
      <div className="row gap-3">
        <div className="grow field">
          <label className="label">Author</label>
          <input className="input" value={author} onChange={(e) => setAuthor(e.target.value)} />
        </div>
        <div className="grow field">
          <label className="label">Genre</label>
          <input className="input" value={genre} onChange={(e) => setGenre(e.target.value)} />
        </div>
      </div>
    </Modal>
  );
}
